import { useAuth } from '@clerk/nextjs'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { InlineWidget } from 'react-calendly'
import { GrafbaseThemes } from 'style'

import { dashboardPaths } from 'domains/website/website.paths'
import { getDocsQuickStartPath } from 'lib/routes/replace-dynamic-paths'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import { IconStyled } from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import Modal from 'components/atomic/molecules/modal'
import LayoutWebsite from 'components/pages/@layouts/layout-website'
import BatteriesIncluded from 'components/pages/@shared/batteries-included'
import SharedCta from 'components/pages/@shared/cta'
import Divider from 'components/pages/@shared/divider'

const SharedFrameworks = dynamic(
  () => import('../../components/pages/@shared/frameworks'),
  { ssr: false },
)

const p = 125

const BFFPage = () => {
  const { isSignedIn } = useAuth()

  return (
    <LayoutWebsite
      meta={{
        title: 'Backend for Frontend',
        description:
          'Build frontends faster with a single GraphQL API, deployed to the edge, that can read from and write to any database or existing API.',
        imageUrl: '/images/og/bff.png',
      }}
      paddingTop={false}
    >
      <Box
        as="section"
        relative
        css={{
          overflow: 'hidden',
        }}
      >
        <Box
          relative
          css={{
            zIndex: 1,
            p: 6,
            paddingTop: '$28',
            paddingBottom: '$24',
            '@lg': {
              paddingTop: '$60',
              paddingBottom: '$60',
              backgroundImage: 'url("/images/bg/bg-bff-light.png")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundSize: 'contain',
              [`.${GrafbaseThemes.dark} &`]: {
                backgroundImage: 'url("/images/bg/bg-bff.png")',
              },
            },
          }}
        >
          <Box flex="column" justify="center" items="center">
            <Text
              as="h1"
              weight="bold"
              css={{
                mt: '$6',
                fontSize: 50,
                lineHeight: 0.9,
                letterSpacing: '$tighter',
                '@lg': {
                  fontSize: 78,
                  lineHeight: 1,
                },
              }}
              align="center"
            >
              Backend for Frontend
            </Text>
            <Text
              as="p"
              size="lg"
              color="text3"
              align="center"
              css={{ container: 'md', mt: '$4' }}
            >
              Build frontends faster with a single GraphQL API, deployed to the
              edge, that can read from and write to any database or existing
              API.
            </Text>
            <Box
              flex
              items="center"
              justify="center"
              css={{
                mt: '$8',
                gap: '$12',
              }}
            >
              <Box
                as={Link}
                href={
                  isSignedIn ? getDocsQuickStartPath() : dashboardPaths.register
                }
              >
                <Button variant="gradient" size="lg">
                  Get Started
                </Button>
              </Box>
              <Modal.Root>
                <Modal.Trigger>
                  <Text underlineHover color="text3">
                    Schedule Demo{' '}
                    <IconStyled
                      as={ArrowRightIcon}
                      css={{ display: 'inline' }}
                    />
                  </Text>
                </Modal.Trigger>
                <Modal.Content
                  size="lg"
                  css={{
                    height: '100%',
                    '@sm': { height: 'unset' },
                    bgc: 'rgb(243 244 246)',
                    '.dark &': { bgc: 'rgb(16 16 16)' },
                    [`> ${Box}`]: {
                      height: '90vh',
                      '.calendly-inline-widget': {
                        height: 'inherit !important',
                      },
                      '@md': {
                        height: 450,
                        position: 'relative',
                        overflow: 'hidden',
                        '.calendly-inline-widget': {
                          height: '700px !important',
                          position: 'absolute',
                          top: -70,
                          left: 0,
                          right: 0,
                        },
                      },
                    },
                  }}
                >
                  <InlineWidget url="https://cal.com/team/grafbase/demo" />
                </Modal.Content>
              </Modal.Root>
            </Box>
          </Box>
          <Box
            absolute
            css={{
              isolation: 'isolate',
              inset: 0,
              zIndex: -1,
              opacity: 0.6,
              overflow: 'hidden',
              pointerEvents: 'none',
              userSelect: 'none',
              '@-moz-document url-prefix()': {
                filter: 'blur(100px)',
              },
            }}
          >
            <Box
              absolute
              css={{
                width: 450,
                height: 750,
                zIndex: -3,
                top: '15%',
                left: '15%',
                filter: 'blur(100px)',
                borderRadius: '$round',
                mixBlendMode: 'screen',
                transform: 'translate3d(0, 0, 0) rotate(-45deg)',
                background:
                  'linear-gradient(180deg, rgba(137, 153, 41, 1) 0%, rgba(81, 20, 144, 0) 30%)',
                [`.${GrafbaseThemes.dark} &`]: {
                  background:
                    'linear-gradient(180deg, rgba(137, 153, 41, 1) 0%, rgba(81, 20, 144, 0) 100%)',
                },
              }}
            />
            <Box
              absolute
              css={{
                width: 450,
                height: 750,
                zIndex: -3,
                top: '15%',
                right: 'calc(45% - 650px)',
                filter: 'blur(100px)',
                borderRadius: '$round',
                mixBlendMode: 'screen',
                transform: 'translate3d(0, 0, 0) rotate(-45deg)',
                background:
                  'linear-gradient(180deg, rgba(137, 153, 41, 1) 0%, rgba(81, 20, 144, 0) 30%)',
                [`.${GrafbaseThemes.dark} &`]: {
                  background:
                    'linear-gradient(180deg, rgba(137, 153, 41, 1) 0%, rgba(81, 20, 144, 0) 100%)',
                },
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box
        style={{
          transform: 'rotate(180deg)',
        }}
      >
        <Divider
          lineBg="linear-gradient(to right, rgb(221 221 221 / 10%) 100%, rgb(33 33 33 / 10%) 100%)"
          lineBgDark="linear-gradient(to right, rgb(126, 130, 128, 0.1) 100%, rgb(20, 23, 22, 0.1) 100%)"
          bg="linear-gradient(250deg, rgb(240 240 240 / 50%) 0%, rgb(214 214 214 / 50%) 100%)"
          bgDark="linear-gradient(250deg, rgb(47, 50, 49, 1) 0%, rgb(20, 23, 22, 0.5) 100%)"
        />
      </Box>
      <BatteriesIncluded />
      <Box
        style={{
          transform: 'rotate(180deg)',
        }}
      >
        <Divider
          lineBg="linear-gradient(to right, rgb(221 221 221 / 10%) 100%, rgb(33 33 33 / 10%) 100%)"
          lineBgDark="linear-gradient(to right, rgb(126, 130, 128, 0.1) 100%, rgb(20, 23, 22, 0.1) 100%)"
          bg="linear-gradient(250deg, rgb(240 240 240 / 50%) 0%, rgb(214 214 214 / 50%) 100%)"
          bgDark="linear-gradient(250deg, rgb(47, 50, 49, 1) 0%, rgb(20, 23, 22, 0.5) 100%)"
        />
      </Box>
      <Box
        as="section"
        css={{
          py: p,
          container: 'xl',
        }}
      >
        <Text
          as="h2"
          size={{ '@initial': '2xl', '@sm': '4xl' }}
          weight="bold"
          align="center"
        >
          Works with your favorite stack
        </Text>
        <Text as="p" size="lg" color="text2" align="center" css={{ mt: '$3' }}>
          Grafbase integrates seamlessly with your existing stack, and with the
          tools you already know and love.
        </Text>
        <Box css={{ mt: '$16' }}>
          <SharedFrameworks />
        </Box>
      </Box>
      <Box as="section">
        <SharedCta />
      </Box>
    </LayoutWebsite>
  )
}

export default BFFPage
